<template>
  <a-layout-sider
    v-model:collapsed="collapsed"
    collapsible
    :width="240"
    :collapsed-width="64"
    :class="b()"
    :trigger="null"
  >
    <!-- Logo area -->
    <div :class="b('logo')">
      <template v-if="isCompanyUser">
        <span v-if="!collapsed" :class="b('logo-text')">{{ companyName }}</span>
        <span v-else :class="b('logo-text', { short: true })">Co</span>
      </template>
      <template v-else>
        <span v-if="!collapsed" :class="b('logo-text')">FS Admin</span>
        <span v-else :class="b('logo-text', { short: true })">FS</span>
      </template>
    </div>

    <!-- Scrollable menu area -->
    <div :class="b('menu-wrap')">
      <!-- Company user sidebar: only their company sub-pages -->
      <a-menu
        v-if="isCompanyUser"
        mode="inline"
        :selected-keys="selectedKeys"
        :class="b('menu')"
      >
        <a-menu-item key="company-dashboard">
          <template #icon><ShopOutlined /></template>
          <router-link :to="`/pod/${companyId}`">Dashboard</router-link>
        </a-menu-item>
        <a-menu-item key="company-page-content">
          <template #icon><LayoutOutlined /></template>
          <router-link :to="`/pod/${companyId}/page-content`">Page Content</router-link>
        </a-menu-item>
        <a-menu-item key="company-collections">
          <template #icon><FolderOutlined /></template>
          <router-link :to="`/pod/${companyId}/product-collections`">Collections</router-link>
        </a-menu-item>
        <a-menu-item key="company-galleries">
          <template #icon><PictureOutlined /></template>
          <router-link :to="`/pod/${companyId}/photo-galleries`">Photo Galleries</router-link>
        </a-menu-item>
        <a-menu-item key="company-orders">
          <template #icon><OrderedListOutlined /></template>
          <router-link :to="`/pod/${companyId}/orders`">Orders</router-link>
        </a-menu-item>
        <a-menu-item key="company-reports">
          <template #icon><FileSearchOutlined /></template>
          <router-link :to="`/pod/${companyId}/reports`">Reports</router-link>
        </a-menu-item>
        <a-menu-item key="company-users">
          <template #icon><TeamOutlined /></template>
          <router-link :to="`/pod/${companyId}/users`">Users</router-link>
        </a-menu-item>
        <a-menu-item key="company-deliveries">
          <template #icon><SendOutlined /></template>
          <router-link :to="`/pod/${companyId}/deliveries`">Deliveries</router-link>
        </a-menu-item>
        <a-menu-item key="company-processing">
          <template #icon><DeploymentUnitOutlined /></template>
          <router-link :to="`/pod/${companyId}/processing`">Processing</router-link>
        </a-menu-item>
        <a-menu-item key="company-help-center">
          <template #icon><QuestionCircleOutlined /></template>
          <router-link :to="`/pod/${companyId}/help-center`">Help Center</router-link>
        </a-menu-item>
        <a-menu-item key="company-page-builder" v-if="false">
          <template #icon><BuildOutlined /></template>
          <router-link :to="`/pod/${companyId}/page-builder`">Page Builder</router-link>
        </a-menu-item>
      </a-menu>

      <!-- Admin sidebar: full navigation grouped by business domain -->
      <a-menu
        v-else
        mode="inline"
        :selected-keys="selectedKeys"
        :open-keys="openKeys"
        :class="b('menu')"
        @update:open-keys="onOpenKeysChange"
      >
        <!-- ORDERS group -->
        <a-sub-menu key="grp-orders">
          <template #icon><UnorderedListOutlined /></template>
          <template #title>Orders</template>

          <!-- Digitize sub-menu -->
          <a-sub-menu key="sub-digitize">
            <template #icon><VideoCameraOutlined /></template>
            <template #title>Digitize</template>
            <a-menu-item key="tape_transfer">
              <router-link :to="`${pageUrls.digitizePage}/tape_transfer`">
                Tape Transfer
              </router-link>
            </a-menu-item>
            <a-menu-item key="film_transfer">
              <router-link :to="`${pageUrls.digitizePage}/film_transfer`">
                Film Transfer
              </router-link>
            </a-menu-item>
            <a-menu-item key="audio_transfer">
              <router-link :to="`${pageUrls.digitizePage}/audio_transfer`">
                Audio Transfer
              </router-link>
            </a-menu-item>
            <a-menu-item key="digital_transfer">
              <router-link :to="`${pageUrls.digitizePage}/digital_transfer`">
                Digital Transfer
              </router-link>
            </a-menu-item>
            <a-menu-item key="photo_scan">
              <router-link :to="`${pageUrls.digitizePage}/photo_scan`">
                Photo Scanning
              </router-link>
            </a-menu-item>
            <a-menu-item key="forever_cloud">
              <router-link :to="`${pageUrls.digitizePage}/forever_cloud`">
                Forever Cloud
              </router-link>
            </a-menu-item>
          </a-sub-menu>

          <!-- Enhance sub-menu -->
          <a-sub-menu key="sub-enhance">
            <template #icon><HighlightOutlined /></template>
            <template #title>Enhance</template>
            <a-menu-item key="photo_restoration">
              <router-link :to="`${pageUrls.enhancePage}/photo_restoration`">
                Photo Restoration
              </router-link>
            </a-menu-item>
            <a-menu-item key="photo_retouching">
              <router-link :to="`${pageUrls.enhancePage}/photo_retouching`">
                Photo Retouching
              </router-link>
            </a-menu-item>
          </a-sub-menu>

          <!-- Deliveries -->
          <a-menu-item key="deliveries">
            <template #icon><SendOutlined /></template>
            <router-link :to="pageUrls.deliveriesPage">Deliveries</router-link>
          </a-menu-item>

          <!-- Rushes -->
          <a-menu-item key="rushes">
            <template #icon><ThunderboltOutlined /></template>
            <router-link :to="pageUrls.rushesPage">Rushes</router-link>
          </a-menu-item>
        </a-sub-menu>

        <!-- PRODUCTS group -->
        <a-sub-menu key="grp-products">
          <template #icon><ShoppingOutlined /></template>
          <template #title>Products</template>

          <a-menu-item key="create-templates">
            <template #icon><AppstoreAddOutlined /></template>
            <router-link :to="pageUrls.createPage">Create Templates</router-link>
          </a-menu-item>

          <a-menu-item key="collections">
            <template #icon><FolderOutlined /></template>
            <router-link :to="pageUrls.collectionsPage">Collections</router-link>
          </a-menu-item>

          <!-- Gift Cards sub-menu -->
          <a-sub-menu key="sub-gift-cards">
            <template #icon><GiftOutlined /></template>
            <template #title>Gift Cards</template>
            <a-menu-item key="gift_card">
              <router-link :to="`${pageUrls.giftCardPage}/gift_card`">
                Gift Card
              </router-link>
            </a-menu-item>
            <a-menu-item key="gift_images">
              <router-link :to="`${pageUrls.giftCardPage}/images`">
                Gift Images
              </router-link>
            </a-menu-item>
            <a-menu-item key="gift_wrapper">
              <router-link :to="`${pageUrls.giftCardPage}/wrapper`">
                Gift Wrapper
              </router-link>
            </a-menu-item>
          </a-sub-menu>

          <a-menu-item key="pods">
            <template #icon><ShopOutlined /></template>
            <router-link :to="pageUrls.companiesPage">PODs</router-link>
          </a-menu-item>
        </a-sub-menu>

        <!-- USERS group -->
        <a-sub-menu key="grp-users">
          <template #icon><TeamOutlined /></template>
          <template #title>Users</template>

          <a-menu-item key="users">
            <template #icon><UserOutlined /></template>
            <router-link :to="pageUrls.usersPage">Users</router-link>
          </a-menu-item>

          <a-menu-item key="permissions">
            <template #icon><SafetyOutlined /></template>
            <router-link :to="pageUrls.permissionsPage">Permissions</router-link>
          </a-menu-item>
        </a-sub-menu>

        <!-- CONTENT group -->
        <a-sub-menu key="grp-content">
          <template #icon><EditOutlined /></template>
          <template #title>Content</template>

          <a-menu-item key="articles">
            <template #icon><FileTextOutlined /></template>
            <router-link :to="`${pageUrls.blogsPage}/articles`">Articles</router-link>
          </a-menu-item>

          <a-menu-item key="categories">
            <template #icon><TagsOutlined /></template>
            <router-link :to="`${pageUrls.blogsPage}/categories`">Categories</router-link>
          </a-menu-item>

          <a-menu-item key="pages-content">
            <template #icon><LayoutOutlined /></template>
            <router-link :to="pageUrls.pagesContent">Pages Content</router-link>
          </a-menu-item>

          <a-menu-item key="help-center">
            <template #icon><QuestionCircleOutlined /></template>
            <router-link :to="pageUrls.helpCenterPage">Help Center</router-link>
          </a-menu-item>
        </a-sub-menu>

        <!-- MARKETING group -->
        <a-sub-menu key="grp-marketing">
          <template #icon><DollarOutlined /></template>
          <template #title>Marketing</template>

          <a-menu-item key="promotions">
            <template #icon><PercentageOutlined /></template>
            <router-link :to="pageUrls.promotionsPage">Promotions</router-link>
          </a-menu-item>

          <a-menu-item key="zip-codes">
            <template #icon><EnvironmentOutlined /></template>
            <router-link :to="pageUrls.zipCodesPage">Zip Codes</router-link>
          </a-menu-item>
        </a-sub-menu>

        <!-- SYSTEM group (admin only) -->
        <a-sub-menu v-if="isAdmin" key="grp-system">
          <template #icon><SettingOutlined /></template>
          <template #title>System</template>

          <!-- Settings sub-menu -->
          <a-sub-menu key="sub-settings">
            <template #icon><ControlOutlined /></template>
            <template #title>Settings</template>
            <a-menu-item key="statuses">
              <router-link :to="`${pageUrls.settingsPage}/statuses`">
                Statuses
              </router-link>
            </a-menu-item>
            <a-menu-item key="emails">
              <router-link :to="`${pageUrls.settingsPage}/emails`">
                Emails
              </router-link>
            </a-menu-item>
            <a-menu-item key="sms">
              <router-link :to="`${pageUrls.settingsPage}/sms`">
                SMS Templates
              </router-link>
            </a-menu-item>
            <a-menu-item key="messagesTemplates">
              <router-link :to="`${pageUrls.settingsPage}/messagesTemplates`">
                Messages Templates
              </router-link>
            </a-menu-item>
          </a-sub-menu>

          <!-- Error Logs -->
          <a-menu-item key="error-logs">
            <template #icon><BugOutlined /></template>
            <router-link :to="pageUrls.errorLogsPage">Error Logs</router-link>
          </a-menu-item>

          <!-- Analytics sub-menu -->
          <a-sub-menu key="sub-analytics">
            <template #icon><BarChartOutlined /></template>
            <template #title>Analytics</template>
            <a-menu-item key="analyticsDashboard">
              <router-link :to="pageUrls.analyticsDashboard">
                Dashboard
              </router-link>
            </a-menu-item>
            <a-menu-item key="sessionsList">
              <router-link :to="pageUrls.sessionsList">
                Sessions
              </router-link>
            </a-menu-item>
            <a-menu-item key="heatmapsPage">
              <router-link :to="pageUrls.heatmapsPage">
                Heatmaps
              </router-link>
            </a-menu-item>
            <a-menu-item key="frontendErrorsPage">
              <router-link :to="pageUrls.frontendErrorsPage">
                Errors
              </router-link>
            </a-menu-item>
            <a-menu-item key="alertsPage">
              <router-link :to="pageUrls.alertsPage">
                Alerts
              </router-link>
            </a-menu-item>
            <a-menu-item key="analyticsApiKeysPage">
              <router-link :to="pageUrls.analyticsApiKeysPage">
                API Keys
              </router-link>
            </a-menu-item>
          </a-sub-menu>
        </a-sub-menu>
      </a-menu>
    </div>

    <!-- Bottom section: collapse trigger, theme, logout -->
    <div :class="b('bottom')">
      <a-button
        :class="b('collapse-btn')"
        type="text"
        block
        @click="collapsed = !collapsed"
      >
        <MenuUnfoldOutlined v-if="collapsed" />
        <template v-else>
          <MenuFoldOutlined />
          <span style="margin-left: 8px;">Collapse</span>
        </template>
      </a-button>
      <ThemeToggle v-if="!collapsed" />
      <a-button
        :class="b('logout')"
        type="text"
        danger
        block
        @click="handleLogout"
      >
        <template #icon><LogoutOutlined /></template>
        <span v-if="!collapsed">Logout</span>
      </a-button>
    </div>
  </a-layout-sider>
</template>

<script>
import {
  computed, ref, watch, onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import {
  UnorderedListOutlined,
  VideoCameraOutlined,
  HighlightOutlined,
  SendOutlined,
  ThunderboltOutlined,
  ShoppingOutlined,
  AppstoreAddOutlined,
  FolderOutlined,
  GiftOutlined,
  ShopOutlined,
  TeamOutlined,
  UserOutlined,
  SafetyOutlined,
  EditOutlined,
  FileTextOutlined,
  TagsOutlined,
  LayoutOutlined,
  QuestionCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  ControlOutlined,
  BugOutlined,
  BarChartOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PictureOutlined,
  OrderedListOutlined,
  FileSearchOutlined,
  DeploymentUnitOutlined,
  BuildOutlined,
} from '@ant-design/icons-vue';
import ThemeToggle from '@/components/ux/navigation/ThemeToggle.vue';
import bem from '@/utils/bem';
import pageUrls from '@/router/pageUrls';

const b = bem('navigation');

const STORAGE_KEY = 'sidebar-collapsed';

/**
 * Map of menu item keys to their route paths.
 * Used by selectedKeys computed to highlight the active menu item
 * based on the current route, and by getParentSubMenuKey() to
 * auto-open the correct sub-menu on page load.
 */
const menuKeyToPath = {
  // Orders
  tape_transfer: `${pageUrls.digitizePage}/tape_transfer`,
  film_transfer: `${pageUrls.digitizePage}/film_transfer`,
  audio_transfer: `${pageUrls.digitizePage}/audio_transfer`,
  digital_transfer: `${pageUrls.digitizePage}/digital_transfer`,
  photo_scan: `${pageUrls.digitizePage}/photo_scan`,
  forever_cloud: `${pageUrls.digitizePage}/forever_cloud`,
  photo_restoration: `${pageUrls.enhancePage}/photo_restoration`,
  photo_retouching: `${pageUrls.enhancePage}/photo_retouching`,
  deliveries: pageUrls.deliveriesPage,
  rushes: pageUrls.rushesPage,
  // Products
  'create-templates': pageUrls.createPage,
  collections: pageUrls.collectionsPage,
  gift_card: `${pageUrls.giftCardPage}/gift_card`,
  gift_images: `${pageUrls.giftCardPage}/images`,
  gift_wrapper: `${pageUrls.giftCardPage}/wrapper`,
  pods: pageUrls.companiesPage,
  // Users
  users: pageUrls.usersPage,
  permissions: pageUrls.permissionsPage,
  // Content
  articles: `${pageUrls.blogsPage}/articles`,
  categories: `${pageUrls.blogsPage}/categories`,
  'pages-content': pageUrls.pagesContent,
  'help-center': pageUrls.helpCenterPage,
  // Marketing
  promotions: pageUrls.promotionsPage,
  'zip-codes': pageUrls.zipCodesPage,
  // System
  statuses: `${pageUrls.settingsPage}/statuses`,
  emails: `${pageUrls.settingsPage}/emails`,
  sms: `${pageUrls.settingsPage}/sms`,
  messagesTemplates: `${pageUrls.settingsPage}/messagesTemplates`,
  'error-logs': pageUrls.errorLogsPage,
  analyticsDashboard: pageUrls.analyticsDashboard,
  sessionsList: pageUrls.sessionsList,
  heatmapsPage: pageUrls.heatmapsPage,
  frontendErrorsPage: pageUrls.frontendErrorsPage,
  alertsPage: pageUrls.alertsPage,
  analyticsApiKeysPage: pageUrls.analyticsApiKeysPage,
};

/**
 * Map of menu item keys to the sub-menu key that contains them.
 * Lets us auto-expand the right sub-menu when a deep link is loaded.
 */
// Maps menu item keys to their immediate parent sub-menu key
const childToSubMenu = {
  tape_transfer: 'sub-digitize',
  film_transfer: 'sub-digitize',
  audio_transfer: 'sub-digitize',
  digital_transfer: 'sub-digitize',
  photo_scan: 'sub-digitize',
  forever_cloud: 'sub-digitize',
  photo_restoration: 'sub-enhance',
  photo_retouching: 'sub-enhance',
  gift_card: 'sub-gift-cards',
  gift_images: 'sub-gift-cards',
  gift_wrapper: 'sub-gift-cards',
  statuses: 'sub-settings',
  emails: 'sub-settings',
  sms: 'sub-settings',
  messagesTemplates: 'sub-settings',
  analyticsDashboard: 'sub-analytics',
  sessionsList: 'sub-analytics',
  heatmapsPage: 'sub-analytics',
  frontendErrorsPage: 'sub-analytics',
  alertsPage: 'sub-analytics',
  analyticsApiKeysPage: 'sub-analytics',
};

// Maps sub-menus and direct items to their parent group key —
// ensures the group accordion opens when a nested item is active
const itemToGroup = {
  'sub-digitize': 'grp-orders',
  'sub-enhance': 'grp-orders',
  deliveries: 'grp-orders',
  rushes: 'grp-orders',
  'create-templates': 'grp-products',
  collections: 'grp-products',
  'sub-gift-cards': 'grp-products',
  pods: 'grp-products',
  users: 'grp-users',
  permissions: 'grp-users',
  articles: 'grp-content',
  categories: 'grp-content',
  'pages-content': 'grp-content',
  'help-center': 'grp-content',
  promotions: 'grp-marketing',
  'zip-codes': 'grp-marketing',
  'sub-settings': 'grp-system',
  'error-logs': 'grp-system',
  'sub-analytics': 'grp-system',
};

// Company sub-page key suffixes — used to build dynamic paths per company
const companyPageSuffixes = {
  'company-dashboard': '',
  'company-page-content': '/page-content',
  'company-collections': '/product-collections',
  'company-galleries': '/photo-galleries',
  'company-orders': '/orders',
  'company-reports': '/reports',
  'company-users': '/users',
  'company-deliveries': '/deliveries',
  'company-processing': '/processing',
  'company-help-center': '/help-center',
  'company-api-config': '/api-config',
  'company-page-builder': '/page-builder',
};

/**
 * Finds the menu key whose path best matches the given route.
 * Prefers longer (more specific) matches.
 * companyPrefix is e.g. '/pod/1' for company users, null for admins.
 */
function findActiveKey(routePath, companyPrefix) {
  let bestKey = null;
  let bestLen = 0;

  // Check company routes if applicable
  if (companyPrefix) {
    Object.entries(companyPageSuffixes).forEach(([key, suffix]) => {
      const fullPath = `${companyPrefix}${suffix}`;
      if (routePath.startsWith(fullPath) && fullPath.length > bestLen) {
        bestKey = key;
        bestLen = fullPath.length;
      }
    });
    return bestKey;
  }

  // Admin routes
  Object.entries(menuKeyToPath).forEach(([key, path]) => {
    if (routePath.startsWith(path) && path.length > bestLen) {
      bestKey = key;
      bestLen = path.length;
    }
  });
  return bestKey;
}

export default {
  name: 'NavigationBlock',
  components: {
    ThemeToggle,
    UnorderedListOutlined,
    VideoCameraOutlined,
    HighlightOutlined,
    SendOutlined,
    ThunderboltOutlined,
    ShoppingOutlined,
    AppstoreAddOutlined,
    FolderOutlined,
    GiftOutlined,
    ShopOutlined,
    TeamOutlined,
    UserOutlined,
    SafetyOutlined,
    EditOutlined,
    FileTextOutlined,
    TagsOutlined,
    LayoutOutlined,
    QuestionCircleOutlined,
    DollarOutlined,
    PercentageOutlined,
    EnvironmentOutlined,
    SettingOutlined,
    ControlOutlined,
    BugOutlined,
    BarChartOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    PictureOutlined,
    OrderedListOutlined,
    FileSearchOutlined,
    DeploymentUnitOutlined,
    BuildOutlined,
  },
  setup() {
    const store = useStore();
    const route = useRoute();

    // Restore collapsed state from localStorage
    const savedCollapsed = localStorage.getItem(STORAGE_KEY);
    const collapsed = ref(savedCollapsed === 'true');

    // Persist collapsed state whenever it changes
    watch(collapsed, (val) => {
      localStorage.setItem(STORAGE_KEY, String(val));
    });

    const isAdmin = computed(() => {
      const { role, id } = store.state.auth;
      return role === 'admin' || role === 'Admin' || id === 1;
    });

    const isCompanyUser = computed(() => store.getters['auth/isCompanyUser']);
    const companyId = computed(() => store.state.auth.company?.id);
    const companyName = computed(
      () => store.state.auth.company?.displayName
        || store.state.auth.company?.pageKey
        || 'Company',
    );

    // Determine which menu item is active based on the current route path
    const companyPrefix = computed(
      () => (isCompanyUser.value ? `/pod/${companyId.value}` : null),
    );
    const selectedKeys = computed(() => {
      const key = findActiveKey(route.path, companyPrefix.value);
      return key ? [key] : [];
    });

    // Track which sub-menus are expanded
    const openKeys = ref([]);

    /**
     * Auto-open the sub-menu that contains the active item.
     * Called on mount and whenever the route changes so that
     * navigating via browser back/forward keeps the menu in sync.
     */
    function syncOpenKeys() {
      // Don't auto-open sub-menus when collapsed — causes popup flyouts
      if (collapsed.value) return;
      const activeKey = findActiveKey(route.path, companyPrefix.value);
      if (!activeKey) return;

      const keysToOpen = new Set(openKeys.value);

      // Open parent sub-menu (e.g. sub-digitize for tape_transfer)
      const subMenu = childToSubMenu[activeKey];
      if (subMenu) keysToOpen.add(subMenu);

      // Open parent group (e.g. grp-orders for sub-digitize or deliveries)
      const groupKey = itemToGroup[subMenu || activeKey];
      if (groupKey) keysToOpen.add(groupKey);

      openKeys.value = [...keysToOpen];
    }

    onMounted(syncOpenKeys);
    watch(() => route.path, syncOpenKeys);

    function onOpenKeysChange(keys) {
      openKeys.value = keys;
    }

    function handleLogout() {
      store.dispatch('auth/logout');
    }

    return {
      b,
      collapsed,
      isAdmin,
      isCompanyUser,
      companyId,
      companyName,
      selectedKeys,
      openKeys,
      pageUrls,
      onOpenKeysChange,
      handleLogout,
    };
  },
};
</script>

<style scoped lang="scss">
.navigation {
  /* Sidebar fills the full viewport height and stays fixed on the left */
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  background: var(--Background-Main, #fff);
  border-right: 1px solid var(--Border-Light, #e5e7eb);
  font-family: 'Montserrat', sans-serif;
  transition: all 0.2s;

  /* ---- Logo ---- */
  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    flex-shrink: 0;
    border-bottom: 1px solid var(--Border-Light, #e5e7eb);
    padding: 0 16px;
  }

  &__logo-text {
    font-size: 16px;
    font-weight: 700;
    color: var(--Text-Primary, #2b3544);
    letter-spacing: 0.02em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    &_short {
      font-size: 18px;
      letter-spacing: 0;
    }
  }

  /* ---- Scrollable menu wrapper ---- */
  &__menu-wrap {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;

    /* Thin custom scrollbar so the sidebar stays tidy */
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--Border-Color, #d9d9d9);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  /* ---- Bottom pinned section ---- */
  &__bottom {
    flex-shrink: 0;
    border-top: 1px solid var(--Border-Light, #e5e7eb);
    padding: 8px;
  }

  &__collapse-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
    color: var(--Text-Secondary, #7f7f7f);
  }

  &__logout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

/* ---- Ant Design overrides scoped to this sidebar ---- */

/* Use deep selectors because ant renders its own DOM inside the sider */
.navigation :deep(.ant-layout-sider-children) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.navigation :deep(.ant-menu) {
  background: transparent;
  border-inline-end: none !important;
  color: var(--Text-Primary, #2b3544);
}

/* Group sub-menus (ORDERS, PRODUCTS, etc.) have bolder titles */

.navigation :deep(.ant-menu-item),
.navigation :deep(.ant-menu-submenu-title) {
  color: var(--Text-Primary, #2b3544);
  border-radius: 6px;
  margin-inline: 4px;
  width: calc(100% - 8px);
}

.navigation :deep(.ant-menu-item a),
.navigation :deep(.ant-menu-submenu-title a) {
  color: inherit;
  text-decoration: none;
}

.navigation :deep(.ant-menu-item:hover),
.navigation :deep(.ant-menu-submenu-title:hover) {
  background: var(--Background-Secondary, #f5f5f5);
  color: var(--Text-Primary, #2b3544);
}

.navigation :deep(.ant-menu-item-selected) {
  background: var(--Button-Primary, #e8c88f) !important;
  color: var(--Text-Primary, #2b3544) !important;
  font-weight: 600;
}

.navigation :deep(.ant-menu-item-selected a) {
  color: var(--Text-Primary, #2b3544) !important;
}

.navigation :deep(.ant-menu-submenu-selected > .ant-menu-submenu-title) {
  color: var(--Text-Primary, #2b3544);
  font-weight: 600;
}

.navigation :deep(.ant-menu-inline .ant-menu-item),
.navigation :deep(.ant-menu-inline .ant-menu-submenu-title) {
  height: 36px;
  line-height: 36px;
  font-size: 13px;
}

/* Hide the default ant sider trigger since we use our own button */
.navigation :deep(.ant-layout-sider-trigger) {
  display: none;
}

/* Collapsed state: keep icons centered */
</style>
